import {
  AggregatePayslip,
  GetPayslipUserResult,
  Payslip,
  ProcessingStatus,
} from "@/models/payslips.model";
import apiClient from "./apiClient";
import { getUTCDate } from "@/utils/date-format";

export async function getUnprocessedAndItemsAddedPayslips(
  year: number,
  month: number
): Promise<GetPayslipUserResult[]> {
  const res = await apiClient.get("/payslips/unprocessed-and-items-added", {
    params: {
      year,
      month,
    },
  });
  const data: GetPayslipUserResult[] = res.data as GetPayslipUserResult[];
  return data;
}

export async function getPayslip(userId: number, year: number, month: number) {
  const res = await apiClient.get(
    `/users/${userId}/payslips?month=${month}&year=${year}`
  );
  const data: Payslip = res.data as Payslip;
  return data;
}

export async function bulkProcessPayslips(args: {
  payDate: Date;
  payslips: {
    month: number;
    year: number;
    userId: number;
    payslipId?: number;
  }[];
}) {
  const res = await apiClient.post("/payslips/bulk-process", {
    ...args,
    payDate: getUTCDate(args.payDate),
  });
  return res.data;
}

export async function getAllProcessedPayslipsOfUser(userId: number) {
  const res = await apiClient.get(
    `users/${userId}/payslips/processed/aggregates`
  );
  return res.data as AggregatePayslip[];
}

export async function getProcessedPayslipsAggregate(
  year: number,
  month: number
) {
  const res = await apiClient.get("/payslips/processed/aggregates", {
    params: {
      year,
      month,
    },
  });
  return res.data as AggregatePayslip[];
}

export async function updatePayslipStatus(args: {
  id: number;
  newStatus: ProcessingStatus;
}) {
  const res = await apiClient.patch(`/payslips/${args.id}/status`, {
    status: args.newStatus,
  });
  return res.data;
}

export async function generatePayslipPdf(
  payslipId: number,
  name: string,
  payslipMonth: number,
  payslipYear: number
) {
  const res = await apiClient.get(`/payslips/${payslipId}/pdf`, {
    headers: {
      Accept: "application/pdf",
    },
    responseType: "arraybuffer",
  });
  const filename = `${name}-${payslipMonth}-${payslipYear}-payslip.pdf`;
  // Create a Blob from the ArrayBuffer response
  const blob = new Blob([res.data], { type: "application/pdf" });
  const file = new File([blob], filename, { type: "application/pdf" });

  return URL.createObjectURL(file);
}

export async function generatePayslipTablePdf(month: number, year: number) {
  const res = await apiClient.get(`/payslips/processed/pdf-table`, {
    headers: {
      Accept: "application/pdf",
    },
    responseType: "arraybuffer",
    params: {
      month,
      year,
    },
  });
  const filename = `payslip-table-${month}-${year}.pdf`;
  // Create a Blob from the ArrayBuffer response
  const blob = new Blob([res.data], { type: "application/pdf" });
  const file = new File([blob], filename, { type: "application/pdf" });

  return URL.createObjectURL(file);
}

export async function getPayslipTableHtml(month: number, year: number) {
  const res = await apiClient.get(`/payslips/processed/pdf-html`, {
    headers: {
      Accept: "text/html",
    },
    params: {
      month,
      year,
    },
  });
  const newWindow = window.open("", "_blank");
  if (newWindow) {
    newWindow.document.write(res.data);
    newWindow.document.close();
  }
}

export async function updateOrCreatePayslipSnapshotDetails(
  userId: number,
  year: number,
  month: number,
  args?: {
    selfHelpGroupSnapshot?: string,
    basicSalarySnapshot?: number;
    employeeCpfRateSnapshot?: number;
    employerCpfRateSnapshot?: number;
  }
) {
  const res = await apiClient.put(
    `/payslips/${userId}/${year}/${month}`, {
      ...args,
    }
  );
  return res.data as Payslip;
}
